import { useEffect } from 'react'
import { Box, Flex } from '@mantine/core'
import { useForm, zodResolver } from '@mantine/form'
import { notifications } from '@mantine/notifications'
import { useQueryClient } from '@tanstack/react-query'

import { isBudgetCategory } from '@/types/budgets.types'

import { Button } from '@/components/Button/Button'
import { editTransactionFormSchema } from '@/components/EditTransactionModal/EditTransactionModal.schema'
import type {
  EditTransactionFormSchema,
  EditTransactionModalProps,
} from '@/components/EditTransactionModal/EditTransactionModal.types'
import { editTransactionFormDefaultValues } from '@/components/EditTransactionModal/EditTransactionModal.values'
import { Modal } from '@/components/Modal/Modal'
import { SelectInput } from '@/components/SelectInput/SelectInput'
import { TextInput } from '@/components/TextInput/TextInput'
import { Title } from '@/components/Title/Title'

import { BUDGET_CATEGORY_OPTIONS } from '@/constants/budgetCategories.constants'
import { TransactionType } from '@/constants/transactions.constants'

import {
  useBudgetServiceBudgetControllerGetAllBudgetsKey,
  useTransactionServiceTransactionControllerEditTransaction,
  useTransactionServiceTransactionControllerGetTransactionsKey,
} from '@/__generated__/queries'
import { category as BudgetCategory } from '@/__generated__/requests'

import { INSIGHTS_QUERY_KEY } from '@/hooks/useInsightsQuery.hook'

const INVALIDATE_KEYS = [
  useBudgetServiceBudgetControllerGetAllBudgetsKey,
  useTransactionServiceTransactionControllerGetTransactionsKey,
  INSIGHTS_QUERY_KEY,
]

export function EditTransactionModal({
  onClose,
  onTransactionsFound,
  openAllTransactions,
  opened,
  transaction,
  type,
}: EditTransactionModalProps) {
  const queryClient = useQueryClient()

  const editTransactionMutation =
    useTransactionServiceTransactionControllerEditTransaction({
      onSuccess: data => {
        notifications.show({
          color: 'green',
          message: 'Transaction updated successfully',
          title: 'Transaction updated',
        })

        const { otherTransactions, transaction } = data

        if (data.otherTransactions.length > 0) {
          onTransactionsFound({
            category: transaction.category,
            ids: otherTransactions,
            name: transaction.name,
          })
          openAllTransactions()
        } else {
          INVALIDATE_KEYS.forEach(key => {
            queryClient.invalidateQueries({
              queryKey: [key],
            })
          })
        }

        onClose()
      },
    })

  const initialValues = transaction
    ? editTransactionFormDefaultValues(transaction)
    : undefined

  const form = useForm<EditTransactionFormSchema>({
    initialValues,
    validate: zodResolver(editTransactionFormSchema),
  })

  const handleSubmit = form.onSubmit(values => {
    const { category, name } = values

    editTransactionMutation.mutate({
      requestBody:
        type === TransactionType.INCOME ? { name } : { category, name },
      transactionId: transaction?.id ?? 0,
    })
  })

  useEffect(() => {
    if (transaction) {
      form.setValues({
        category: isBudgetCategory(transaction.category)
          ? transaction.category
          : BudgetCategory.OTHER,
        name: transaction.merchant ?? transaction.name,
      })
      form.resetDirty({
        category: isBudgetCategory(transaction.category)
          ? transaction.category
          : BudgetCategory.OTHER,
        name: transaction.merchant ?? transaction.name,
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transaction])

  return (
    <Modal centered onClose={onClose} opened={opened} withCloseButton={false}>
      <Flex justify="center" p={24}>
        <Title mb={8} variant="h1">
          Edit Transaction
        </Title>
      </Flex>

      <Box component="form" noValidate onSubmit={handleSubmit}>
        <TextInput {...form.getInputProps('name')} label="Name" />

        {type !== TransactionType.INCOME && (
          <SelectInput
            {...form.getInputProps('category')}
            data={BUDGET_CATEGORY_OPTIONS}
            label="Category"
            placeholder="Select category"
          />
        )}

        <Flex
          direction={{ base: 'column', md: 'row' }}
          gap={16}
          justify={{ base: 'center', md: 'flex-start' }}
          mt={32}
        >
          <Button miw="48%" onClick={onClose} variant="outline">
            Cancel
          </Button>

          <Button miw="48%" type="submit">
            Save
          </Button>
        </Flex>
      </Box>
    </Modal>
  )
}
